import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiFetch } from '../utils/apiClient';

// API base URL - configurable via environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Stock Research's current score card and its historical Top N comparison both
// use the global ALL ranking universe rather than a portfolio or saved-scan scope.
const STOCK_RESEARCH_BACKTEST_INDEX_CODE = 'ALL';

const BACKTEST_RETURN_HORIZONS = [
  { key: '1y', label: '1Y Return' },
  { key: '2y', label: '2Y Return' },
  { key: '3y', label: '3Y Return' },
  { key: '4y', label: '4Y Return' },
  { key: '6y', label: '6Y Return' },
  { key: '8y', label: '8Y Return' },
  { key: '10y', label: '10Y Return' },
];

const BACKTEST_SUMMARY_HORIZONS = BACKTEST_RETURN_HORIZONS.filter(
  ({ key }) => key !== '4y' && key !== '8y'
);

const RANKING_UNIVERSE_LABELS = {
  ALL: 'All indexed tickers (United States + Canada)',
  SP500: 'S&P 500',
  NASDAQ100: 'Nasdaq-100',
  DOW30: 'Dow 30',
  TSX60: 'S&P/TSX 60',
  TSX_COMPOSITE: 'S&P/TSX Composite',
};

const getRankingUniverseLabel = (code) =>
  code && RANKING_UNIVERSE_LABELS[code]
    ? RANKING_UNIVERSE_LABELS[code]
    : 'Universe not assigned';

function StockResearch() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(Boolean(ticker));
  const [error, setError] = useState('');
  const [selectedRange, setSelectedRange] = useState('10Y');
  const [searchTicker, setSearchTicker] = useState('');
  const [searchError, setSearchError] = useState('');
  
  // User info for tier gating
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  
  // Backtest state
  const [backtestData, setBacktestData] = useState(null);
  const [loadingBacktest, setLoadingBacktest] = useState(false);
  const [backtestError, setBacktestError] = useState('');
  const [selectedRankThreshold, setSelectedRankThreshold] = useState(50);
  const [showBacktest, setShowBacktest] = useState(true);
  
  // Add to Portfolio state
  const [portfolios, setPortfolios] = useState([]);
  const [showAddToPortfolioModal, setShowAddToPortfolioModal] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [holdingData, setHoldingData] = useState({
    quantity: '',
    average_buy_price: ''
  });
  const [addToPortfolioError, setAddToPortfolioError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch user info for tier gating
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/me`, { method: 'GET' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        setUserInfo(data);
      } catch (err) {
        console.error('Failed to fetch user info:', err);
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []);

  // Update search input when route ticker changes
  useEffect(() => {
    if (ticker) {
      setSearchTicker(ticker.toUpperCase());
      setSuccessMessage(''); // Clear success message when ticker changes
      return;
    }

    setSearchTicker('');
    setStockData(null);
    setBacktestData(null);
    setError('');
    setSuccessMessage('');
  }, [ticker]);

  const parseDateOnlyLocal = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString.trim());
    if (!match) {
      const parsed = new Date(dateString);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const getChartDateValue = (point) => point.actual_price_date || point.date;
  
  // Set selectedRange to 1Y for Free users
  useEffect(() => {
    if (!loadingUserInfo && userInfo && userInfo.plan_type === 'free') {
      setSelectedRange('1Y');
    }
  }, [loadingUserInfo, userInfo]);

  // Fetch portfolios on component mount
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/portfolio`, { method: 'GET' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        setPortfolios(data.portfolios || []);
      } catch (err) {
        console.error('Failed to fetch portfolios:', err);
      }
    };

    fetchPortfolios();
  }, []);

  useEffect(() => {
    const fetchStockData = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await apiFetch(`${API_BASE_URL}/stock/${ticker}`, { method: 'GET' });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            typeof errorData.detail === 'string'
              ? errorData.detail
              : JSON.stringify(errorData.detail)
          );
        }

        const data = await response.json();
        setStockData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!ticker) {
      setLoading(false);
      return;
    }

    fetchStockData();
  }, [ticker]);

  const getFilteredPriceHistory = () => {
    if (!stockData || !stockData.price_history || stockData.price_history.length === 0) {
      return [];
    }

    const now = new Date();
    let cutoffDate;

    switch (selectedRange) {
      case '1Y':
        cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case '3Y':
        cutoffDate = new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
        break;
      case '5Y':
        cutoffDate = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
        break;
      case '10Y':
        cutoffDate = new Date(now.getFullYear() - 10, now.getMonth(), now.getDate());
        break;
      default:
        cutoffDate = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
    }

    return stockData.price_history
      .map(point => ({
        ...point,
        chart_date: getChartDateValue(point),
      }))
      .filter(point => {
        const pointDate = parseDateOnlyLocal(point.chart_date);
        if (!pointDate) {
          return false;
        }
        return pointDate >= cutoffDate;
      });
  };

  const getRangeSummary = () => {
    const filteredData = getFilteredPriceHistory();
    if (filteredData.length === 0) {
      return null;
    }

    const startPrice = filteredData[0].close_price;
    const endPrice = filteredData[filteredData.length - 1].close_price;
    const totalReturn = ((endPrice - startPrice) / startPrice) * 100;

    // Calculate max drawdown from filtered data
    let maxDrawdown = 0;
    let peak = filteredData[0].close_price;
    
    for (let i = 0; i < filteredData.length; i++) {
      const price = filteredData[i].close_price;
      if (price > peak) {
        peak = price;
      }
      const drawdown = ((price - peak) / peak) * 100;
      if (drawdown < maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }

    return {
      startPrice,
      endPrice,
      totalReturn,
      maxDrawdown: Math.abs(maxDrawdown)
    };
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(2);
  };

  const formatBacktestReturn = (value) => {
    if (value === null || value === undefined) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // Determine if user is on Free plan
  const isFreePlan = !loadingUserInfo && userInfo && userInfo.plan_type === 'free';

  const fetchBacktest = useCallback(async (threshold) => {
    setLoadingBacktest(true);
    setBacktestError('');
    
    try {
      const queryParams = new URLSearchParams({
        rank_threshold: String(threshold),
        index_code: STOCK_RESEARCH_BACKTEST_INDEX_CODE,
      });
      const response = await apiFetch(
        `${API_BASE_URL}/stock/${ticker}/backtest?${queryParams.toString()}`,
        {
          method: 'GET',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Stock Research backtest request failed', {
          ticker,
          rankThreshold: threshold,
          indexCode: STOCK_RESEARCH_BACKTEST_INDEX_CODE,
          status: response.status,
          errorData,
        });
        throw new Error(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Unable to load historical backtesting data. Please try again.'
        );
      }

      const data = await response.json();
      setBacktestData(data);
    } catch (err) {
      setBacktestError(err.message);
    } finally {
      setLoadingBacktest(false);
    }
  }, [ticker]);

  useEffect(() => {
    if (
      !showBacktest ||
      !ticker ||
      loadingUserInfo ||
      !userInfo ||
      userInfo.plan_type === 'free' ||
      backtestData ||
      loadingBacktest ||
      backtestError
    ) {
      return;
    }

    fetchBacktest(selectedRankThreshold);
  }, [
    showBacktest,
    ticker,
    loadingUserInfo,
    userInfo,
    backtestData,
    loadingBacktest,
    backtestError,
    selectedRankThreshold,
    fetchBacktest,
  ]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchError('');
    
    const trimmedTicker = searchTicker.trim().toUpperCase();
    
    if (!trimmedTicker) {
      setSearchError('Please enter a ticker symbol');
      return;
    }
    
    navigate(`/stock/${trimmedTicker}`);
  };

  const handleOpenAddToPortfolio = () => {
    setShowAddToPortfolioModal(true);
    setAddToPortfolioError('');
    setSelectedPortfolio('');
    setHoldingData({
      quantity: '',
      average_buy_price: ''
    });
  };

  const handleCancelAddToPortfolio = () => {
    setShowAddToPortfolioModal(false);
    setAddToPortfolioError('');
    setSelectedPortfolio('');
    setHoldingData({
      quantity: '',
      average_buy_price: ''
    });
  };

  const handleConfirmAddToPortfolio = async () => {
    setAddToPortfolioError('');
    
    // Validate portfolio selection
    if (!selectedPortfolio) {
      setAddToPortfolioError('Please select a portfolio');
      return;
    }

    // Validate inputs
    if (!holdingData.quantity || !holdingData.average_buy_price) {
      setAddToPortfolioError('Please enter both Quantity and Average Buy Price');
      return;
    }
    
    const quantity = parseFloat(holdingData.quantity);
    const avgPrice = parseFloat(holdingData.average_buy_price);
    
    if (isNaN(quantity) || quantity <= 0) {
      setAddToPortfolioError('Quantity must be a positive number');
      return;
    }
    
    if (isNaN(avgPrice) || avgPrice <= 0) {
      setAddToPortfolioError('Average Buy Price must be a positive number');
      return;
    }
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/portfolio/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portfolio_id: parseInt(selectedPortfolio),
          ticker: ticker.toUpperCase(),
          quantity: quantity,
          average_buy_price: avgPrice
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'string'
          ? errorData.detail
          : JSON.stringify(errorData.detail);
        setAddToPortfolioError(errorMessage);
        return;
      }

      // Success - close modal and show success message
      setShowAddToPortfolioModal(false);
      setAddToPortfolioError('');
      setSelectedPortfolio('');
      setHoldingData({
        quantity: '',
        average_buy_price: ''
      });
      
      // Refresh portfolios list
      const portfoliosResponse = await apiFetch(`${API_BASE_URL}/portfolio`, { method: 'GET' });

      if (portfoliosResponse.ok) {
        const portfoliosData = await portfoliosResponse.json();
        setPortfolios(portfoliosData.portfolios || []);
      }
      
      // Show success message
      setSuccessMessage(`${ticker.toUpperCase()} added to portfolio successfully!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      setAddToPortfolioError(`Failed to add ${ticker.toUpperCase()}: ${err.message}`);
    }
  };

  return (
    <div className="stock-research-page authenticated-page">
      {/* Page Header */}
      <div className="stock-research-container authenticated-page-content">
        <div className="stock-research-header">
          <div className="stock-research-header-content">
            <h1 className="stock-research-title">Stock Research</h1>
            <p className="stock-research-subtitle">
              Analyze stocks with our proprietary value-investing methodology
            </p>
          </div>
        </div>
      </div>

      {/* Ticker Search Card */}
      <div className="stock-research-container authenticated-page-content">
        <div className="stock-research-search-card">
          <h3 className="stock-research-search-title">
            Search Another Stock
          </h3>
        <form onSubmit={handleSearchSubmit} className="stock-research-search-form">
          <input
            type="text"
            value={searchTicker}
            onChange={(e) => {
              setSearchTicker(e.target.value);
              setSearchError('');
            }}
            placeholder="Enter ticker symbol (e.g., AAPL, MSFT, GOOGL)"
            className="stock-research-search-input"
          />
          <button
            type="submit"
            className="stock-research-standard-button stock-research-search-button"
          >
            View Stock
          </button>
        </form>
        {searchError && (
          <p className="stock-research-search-error">
            {searchError}
          </p>
        )}
        </div>
      </div>

      <div className="stock-research-container authenticated-page-content">
        {loading && <p className="stock-research-loading">Loading stock data...</p>}
        {error && <div className="stock-research-error">{error}</div>}
        
        {successMessage && (
          <div className="stock-research-success-message">
            {successMessage}
          </div>
        )}
      </div>

      {/* Add to Portfolio Modal */}
      {showAddToPortfolioModal && (
        <div className="stock-research-modal-overlay">
          <div className="stock-research-modal">
            <h3 className="stock-research-modal-title">Add {ticker?.toUpperCase()} to Portfolio</h3>
            
            {addToPortfolioError && (
              <div className="stock-research-modal-error">
                {addToPortfolioError}
              </div>
            )}
            
            {portfolios.length === 0 ? (
              <>
                <p className="stock-research-modal-empty-message">
                  You don’t have a portfolio yet. Create one on the{' '}
                  <Link to="/portfolios" className="theme-link">Portfolios</Link>
                  {' '}page, then come back here to add this stock.
                </p>
                <div className="stock-research-modal-actions">
                  <button
                    onClick={handleCancelAddToPortfolio}
                    className="stock-research-standard-button stock-research-modal-button stock-research-modal-button-cancel"
                  >
                    Ok
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="stock-research-modal-field">
                  <label className="stock-research-modal-label">
                    Portfolio: <span className="stock-research-modal-required">*</span>
                  </label>
                  <select
                    value={selectedPortfolio}
                    onChange={(e) => {
                      setAddToPortfolioError('');
                      setSelectedPortfolio(e.target.value);
                    }}
                    className="stock-research-modal-select"
                  >
                    <option value="">-- Select a Portfolio --</option>
                    {portfolios.map((portfolio) => (
                      <option
                        key={portfolio.id}
                        value={portfolio.id}
                      >
                        {portfolio.name} - {getRankingUniverseLabel(portfolio.ranking_universe_code)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="stock-research-modal-field">
                  <label className="stock-research-modal-label">
                    Quantity: <span className="stock-research-modal-required">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={holdingData.quantity}
                    onChange={(e) => {
                      setAddToPortfolioError('');
                      setHoldingData({
                        ...holdingData,
                        quantity: e.target.value
                      });
                    }}
                    placeholder="e.g., 100"
                    className="stock-research-modal-input"
                  />
                </div>

                <div className="stock-research-modal-field">
                  <label className="stock-research-modal-label">
                    Average Buy Price: <span className="stock-research-modal-required">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={holdingData.average_buy_price}
                    onChange={(e) => {
                      setAddToPortfolioError('');
                      setHoldingData({
                        ...holdingData,
                        average_buy_price: e.target.value
                      });
                    }}
                    placeholder="e.g., 150.50"
                    className="stock-research-modal-input"
                  />
                </div>

                <div className="stock-research-modal-actions">
                  <button
                    onClick={handleCancelAddToPortfolio}
                    className="stock-research-standard-button stock-research-modal-button stock-research-modal-button-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmAddToPortfolio}
                    className="stock-research-standard-button stock-research-modal-button stock-research-modal-button-confirm"
                  >
                    Add to Portfolio
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!loading && stockData && (
        <div className="stock-research-container authenticated-page-content">
          {/* Stock Identity Header Card */}
          <div className="stock-research-identity-card">
            <div className="stock-research-identity-content">
              {/* Left Column - Stock Information */}
              <div className="stock-research-identity-info">
                <h2 className="stock-research-ticker">
                  {stockData.ticker}
                </h2>
                {stockData.fundamentals && (
                  <>
                    <p className="stock-research-company-name">
                      {stockData.fundamentals.company_name || 'N/A'}
                    </p>
                    <p className="stock-research-sector">
                      <strong>Sector:</strong> {stockData.fundamentals.sector || 'N/A'}
                    </p>
                    <p className="stock-research-current-price">
                      {stockData.fundamentals.current_price !== null && stockData.fundamentals.current_price !== undefined
                        ? `$${stockData.fundamentals.current_price.toFixed(2)}`
                        : 'N/A'
                      }
                    </p>
                  </>
                )}
              </div>
              
              {/* Right Column - Add to Portfolio Button */}
              <div className="stock-research-identity-actions">
                <button
                  onClick={handleOpenAddToPortfolio}
                  className="stock-research-standard-button stock-research-add-portfolio-button"
                >
                  + Add to Portfolio
                </button>
              </div>
            </div>
          </div>

          {/* Backtesting Section - Pro/Premium Only */}
          {!loadingUserInfo && userInfo && userInfo.plan_type === 'free' ? (
            <div className="stock-research-section stock-research-teaser-card">
              <h3 className="stock-research-teaser-title">
                Historical Backtesting
              </h3>
              <p className="stock-research-teaser-text">
                Historical backtesting is available on Pro and Premium plans.
              </p>
            </div>
          ) : (
            <div className="stock-research-section">
              <div className="stock-research-backtest-header">
                <h3 className="stock-research-section-title">
                  Historical Backtesting
                </h3>
                <button
                  onClick={() => {
                    const newShowState = !showBacktest;
                    setShowBacktest(newShowState);
                    if (newShowState && !backtestData) {
                      fetchBacktest(selectedRankThreshold);
                    }
                  }}
                  className="stock-research-standard-button stock-research-btn-toggle"
                >
                  {showBacktest ? 'Hide Backtest' : 'Show Backtest'}
                </button>
              </div>

              {showBacktest && (
                <>
                  <p className="stock-research-backtest-subtitle">
                    View how this stock performed after it historically ranked within the selected Investor Indicator Top N threshold.
                  </p>

                  {/* Threshold Selector */}
                  <div className="stock-research-threshold-selector">
                    <div className="stock-research-threshold-buttons">
                      {[10, 25, 50, 100].map(threshold => (
                        <button
                          key={threshold}
                          onClick={() => {
                            setSelectedRankThreshold(threshold);
                            fetchBacktest(threshold);
                          }}
                          className={`stock-research-threshold-btn ${selectedRankThreshold === threshold ? 'active' : ''}`}
                        >
                          Top {threshold}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* General Explanation - Pro+Premium */}
                  <div className="stock-research-backtest-explanation">
                    <p>
                      <strong>How to read this:</strong>{' '}Choose a Top N tab to see when this stock ranked within that group. For example, Top 10 shows dates when the stock was among the model’s 10 highest-ranked stocks, while Top 100 includes a wider group. Each row is one past ranking date. The return columns show how the stock performed over the next 1, 2, 3, 4, 6, 8, or 10 years when enough data is available. The summary cards use only the results from the selected Top N. A high historical rank means the model viewed the stock favorably at that time. The results below show how the stock performed after those signals.
                    </p>
                  </div>

                  {loadingBacktest && <p className="stock-research-backtest-loading">Loading backtest data...</p>}
                  {backtestError && <p className="stock-research-backtest-error">{backtestError}</p>}

                  {backtestData && (
                    <>
                      {backtestData.qualifying_snapshot_count === 0 ? (
                        <>
                          <div className="stock-research-backtest-no-qualify">
                            <p>
                              {ticker} did not qualify within the historical Top {selectedRankThreshold} threshold in the available backtest snapshots.
                            </p>
                          </div>
                          
                          {/* Premium Interpretation for zero qualifications */}
                          {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && backtestData.backtest_interpretation && (
                            <div className="validation-lab-interpretation-card">
                              <h4 className="validation-lab-interpretation-title">
                                What the Results Mean?
                              </h4>
                              <p className="validation-lab-interpretation-text">
                                {backtestData.backtest_interpretation}
                              </p>
                            </div>
                          )}
                          
                          {/* Pro Teaser for Premium Backtesting Interpretation - zero qualifications */}
                          {!loadingUserInfo && userInfo && userInfo.plan_type === 'pro' && (
                            <div className="stock-research-premium-teaser">
                              <h3 className="stock-research-premium-teaser-title">
                                What the Results Mean?
                              </h3>
                              <p className="stock-research-premium-teaser-text">
                                Premium adds a ticker-specific explanation of what this stock's historical backtesting results suggest at the selected Top N threshold, including how often it qualified, whether later returns were favorable or mixed, how much long-term evidence is available, and how to read the pattern in a fundamentals-driven long-term investing context.
                              </p>
                              <p className="stock-research-premium-teaser-note">
                                Available on Premium.
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Summary Cards */}
                          <div className="stock-research-backtest-summary-grid">
                            <div className="stock-research-backtest-summary-card stock-research-backtest-summary-primary">
                              <div className="stock-research-backtest-summary-label">
                                Total Qualifications
                              </div>
                              <div className="stock-research-backtest-summary-value">
                                {backtestData.qualifying_snapshot_count}
                              </div>
                            </div>

                            {BACKTEST_SUMMARY_HORIZONS.map(({ key: horizon }) => {
                              const summary = backtestData.summary?.[horizon];
                              const hasCompletedReturns = Boolean(
                                summary &&
                                summary.available_return_count > 0 &&
                                summary.average_total_return_percent !== null &&
                                summary.average_total_return_percent !== undefined
                              );
                              const avgReturn = hasCompletedReturns
                                ? summary.average_total_return_percent
                                : null;
                              const winRate = hasCompletedReturns
                                ? ((summary.positive_return_count ?? 0) / summary.available_return_count * 100)
                                : null;

                              return (
                                <div
                                  key={horizon}
                                  className={`stock-research-backtest-summary-card ${
                                    hasCompletedReturns ? (avgReturn >= 0 ? 'positive' : 'negative') : ''
                                  }`}
                                >
                                  <div className="stock-research-backtest-summary-label">
                                    {horizon.toUpperCase()} Avg Return
                                  </div>
                                  <div className="stock-research-backtest-summary-value">
                                    {formatBacktestReturn(avgReturn)}
                                  </div>
                                  {hasCompletedReturns && (
                                    <div className="stock-research-backtest-summary-winrate">
                                      Win Rate: {winRate.toFixed(0)}%
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Premium Interpretation for positive qualifications */}
                          {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && backtestData.backtest_interpretation && (
                            <div className="validation-lab-interpretation-card">
                              <h4 className="validation-lab-interpretation-title">
                                What the Results Mean?
                              </h4>
                              <p className="validation-lab-interpretation-text">
                                {backtestData.backtest_interpretation}
                              </p>
                            </div>
                          )}
                          
                          {/* Pro Teaser for Premium Backtesting Interpretation - positive qualifications */}
                          {!loadingUserInfo && userInfo && userInfo.plan_type === 'pro' && (
                            <div className="stock-research-premium-teaser">
                              <h3 className="stock-research-premium-teaser-title">
                                What the Results Mean?
                              </h3>
                              <p className="stock-research-premium-teaser-text">
                                Premium adds a ticker-specific explanation of what this stock's historical backtesting results suggest at the selected Top N threshold, including how often it qualified, whether later returns were favorable or mixed, how much long-term evidence is available, and how to read the pattern in a fundamentals-driven long-term investing context.
                              </p>
                              <p className="stock-research-premium-teaser-note">
                                Available on Premium.
                              </p>
                            </div>
                          )}

                          {/* Historical Qualifications Table */}
                          {backtestData.historical_qualifications && backtestData.historical_qualifications.length > 0 && (
                            <div className="stock-research-backtest-table-section">
                              <h4 className="stock-research-backtest-table-title">
                                Historical Qualifications
                              </h4>
                              <div className="stock-research-backtest-table-wrapper">
                                <table className="stock-research-backtest-table">
                                  <thead>
                                    <tr className="stock-research-backtest-table-header">
                                      <th className="stock-research-backtest-th stock-research-backtest-th-date">
                                        Snapshot Date
                                      </th>
                                      <th className="stock-research-backtest-th stock-research-backtest-th-rank">
                                        Global Rank
                                      </th>
                                      <th className="stock-research-backtest-th stock-research-backtest-th-score">
                                        Investment Score
                                      </th>
                                      {BACKTEST_RETURN_HORIZONS.map(({ key, label }) => (
                                        <th
                                          key={key}
                                          className="stock-research-backtest-th stock-research-backtest-th-return"
                                        >
                                          {label}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {backtestData.historical_qualifications.map((qual, index) => (
                                      <tr key={index} className="stock-research-backtest-tr">
                                        <td className="stock-research-backtest-td stock-research-backtest-td-date">
                                          {new Date(qual.snapshot_date).toLocaleDateString()}
                                        </td>
                                        <td className="stock-research-backtest-td stock-research-backtest-td-rank">
                                          {qual.historical_rank}
                                        </td>
                                        <td className="stock-research-backtest-td stock-research-backtest-td-score">
                                          {qual.investment_score !== null && qual.investment_score !== undefined 
                                            ? qual.investment_score.toFixed(1) 
                                            : '—'
                                          }
                                        </td>
                                        {BACKTEST_RETURN_HORIZONS.map(({ key: horizon }) => {
                                          const returnData = qual.forward_returns?.[horizon];
                                          const returnPct = returnData?.total_return_percent;
                                          
                                          return (
                                            <td 
                                              key={horizon}
                                              className={`stock-research-backtest-td stock-research-backtest-td-return ${
                                                returnPct !== null && returnPct !== undefined 
                                                  ? (returnPct >= 0 ? 'positive' : 'negative')
                                                  : ''
                                              }`}
                                            >
                                              {formatBacktestReturn(returnPct)}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="stock-research-backtest-tr stock-research-backtest-average-row">
                                      <td className="stock-research-backtest-td stock-research-backtest-td-date">
                                        Average
                                      </td>
                                      <td className="stock-research-backtest-td stock-research-backtest-td-rank">
                                        —
                                      </td>
                                      <td className="stock-research-backtest-td stock-research-backtest-td-score">
                                        —
                                      </td>
                                      {BACKTEST_RETURN_HORIZONS.map(({ key: horizon }) => {
                                        const horizonSummary = backtestData.summary?.[horizon];
                                        const averageReturn =
                                          horizonSummary?.available_return_count > 0
                                            ? horizonSummary.average_total_return_percent
                                            : null;

                                        return (
                                          <td
                                            key={horizon}
                                            className={`stock-research-backtest-td stock-research-backtest-td-return ${
                                              averageReturn !== null && averageReturn !== undefined
                                                ? (averageReturn >= 0 ? 'positive' : 'negative')
                                                : ''
                                            }`}
                                          >
                                            {formatBacktestReturn(averageReturn)}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Current Investment Snapshot Section */}
          <div className="stock-research-section">
            <h3 className="stock-research-section-title">
              Current Investment Snapshot
            </h3>
            {stockData.current_investment_snapshot ? (
              <>
                <p className="stock-research-scan-date">
                  Current operational model output from {new Date(stockData.current_investment_snapshot.snapshot_at).toLocaleString()}
                </p>
                <p className="stock-research-scan-date">
                  Global Rank #{stockData.current_investment_snapshot.global_rank ?? 'N/A'}
                </p>
                <div className="stock-research-score-grid">
                  {/* Investment Score - Primary Badge */}
                  <div className="stock-research-score-card stock-research-score-primary">
                    <div className="stock-research-score-label">Investment Score</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.investment_score !== null &&
                       stockData.current_investment_snapshot.investment_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.investment_score)
                        ? stockData.current_investment_snapshot.investment_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                  
                  {/* Valuation Score */}
                  <div className="stock-research-score-card">
                    <div className="stock-research-score-label">Valuation</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.valuation_score !== null &&
                       stockData.current_investment_snapshot.valuation_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.valuation_score)
                        ? stockData.current_investment_snapshot.valuation_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                  
                  {/* Profitability Score */}
                  <div className="stock-research-score-card">
                    <div className="stock-research-score-label">Profitability</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.profitability_score !== null &&
                       stockData.current_investment_snapshot.profitability_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.profitability_score)
                        ? stockData.current_investment_snapshot.profitability_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                  
                  {/* Balance Sheet Score */}
                  <div className="stock-research-score-card">
                    <div className="stock-research-score-label">Balance Sheet</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.balance_sheet_score !== null &&
                       stockData.current_investment_snapshot.balance_sheet_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.balance_sheet_score)
                        ? stockData.current_investment_snapshot.balance_sheet_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                  
                  {/* Growth / Cash Flow Score */}
                  <div className="stock-research-score-card">
                    <div className="stock-research-score-label">Growth / Cash Flow</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.growth_cashflow_score !== null &&
                       stockData.current_investment_snapshot.growth_cashflow_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.growth_cashflow_score)
                        ? stockData.current_investment_snapshot.growth_cashflow_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                  
                  {/* Risk / Stability Score */}
                  <div className="stock-research-score-card">
                    <div className="stock-research-score-label">Risk / Stability</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.risk_stability_score !== null &&
                       stockData.current_investment_snapshot.risk_stability_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.risk_stability_score)
                        ? stockData.current_investment_snapshot.risk_stability_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                  
                  {/* Return Consistency Score */}
                  <div className="stock-research-score-card">
                    <div className="stock-research-score-label">Return Consistency</div>
                    <div className="stock-research-score-value">
                      {stockData.current_investment_snapshot.return_consistency_score !== null &&
                       stockData.current_investment_snapshot.return_consistency_score !== undefined &&
                       !isNaN(stockData.current_investment_snapshot.return_consistency_score)
                        ? stockData.current_investment_snapshot.return_consistency_score.toFixed(1)
                        : 'N/A'
                      }
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="stock-research-no-data">
                <p>
                  Current operational investment snapshot is unavailable for this stock.
                </p>
              </div>
            )}
          </div>

          {/* Investment Score Analysis Section - Premium Only */}
          {stockData.current_investment_snapshot && !loadingUserInfo && userInfo && (
            <>
              {userInfo.plan_type === 'unlimited' ? (
                // Premium users: show explanation if available
                stockData.current_investment_snapshot.investment_score_explanation && (
                  <div className="stock-research-section">
                    <h3 className="stock-research-section-title">
                      Investment Score Analysis
                    </h3>
                    <p className="stock-research-analysis-text">
                      {stockData.current_investment_snapshot.investment_score_explanation}
                    </p>
                  </div>
                )
              ) : (
                // Free and Pro users: show teaser
                <div className="stock-research-premium-teaser">
                  <h3 className="stock-research-premium-teaser-title">
                    Investment Score Analysis
                  </h3>
                  <p className="stock-research-premium-teaser-text">
                    Premium adds a written explanation of why this stock's Investment Score is strong, moderate, or weak, including the main score drivers and the factors holding it back.
                  </p>
                  <p className="stock-research-premium-teaser-note">
                    Available on Premium.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Price Chart Container Card */}
          {stockData.price_history && stockData.price_history.length > 0 && (
            <div className="stock-research-section">
              <h3 className="stock-research-section-title">
                Long-Term Monthly Price History
              </h3>
              <p className="stock-research-section-subtitle">
                Monthly close prices from internal historical price data.
              </p>
              
              {/* Range Buttons */}
              <div className="stock-research-chart-controls">
                <div className="stock-research-range-buttons">
                  {isFreePlan ? (
                    // Free users: only 1Y button
                    <button
                      onClick={() => setSelectedRange('1Y')}
                      className="stock-research-standard-button stock-research-range-btn active"
                    >
                      1Y
                    </button>
                  ) : (
                    // Pro/Premium users: all range buttons
                    ['1Y', '3Y', '5Y', '10Y'].map(range => (
                      <button
                        key={range}
                        onClick={() => setSelectedRange(range)}
                        className={`stock-research-standard-button stock-research-range-btn ${selectedRange === range ? 'active' : ''}`}
                      >
                        {range}
                      </button>
                    ))
                  )}
                </div>
                {isFreePlan && (
                  <p className="stock-research-chart-note">
                    Free Preview includes the 1Y chart only. Upgrade to Pro or Premium for 3Y, 5Y, and 10Y history.
                  </p>
                )}
              </div>

              {/* Chart */}
              <div className="stock-research-chart-wrapper">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={getFilteredPriceHistory()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(71, 85, 105, 0.3)" />
                    <XAxis
                      dataKey="chart_date"
                      tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                      stroke="var(--color-border-subtle)"
                      tickFormatter={(date) => {
                        const d = parseDateOnlyLocal(date);
                        return d ? `${d.getMonth() + 1}/${d.getFullYear()}` : date;
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                      stroke="var(--color-border-subtle)"
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'var(--color-surface-darker)',
                        border: '1px solid var(--color-border-subtle)',
                        borderRadius: '0.375rem',
                        color: 'var(--color-text-primary)'
                      }}
                      formatter={(value) => [`$${value.toFixed(2)}`, 'Close Price']}
                      labelFormatter={(date) => {
                        const parsedDate = parseDateOnlyLocal(date);
                        return parsedDate ? parsedDate.toLocaleDateString() : date;
                      }}
                      labelStyle={{ color: 'var(--color-accent-primary)' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="close_price" 
                      stroke="var(--color-accent-primary)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Range Summary Cards */}
              {getRangeSummary() && (
                <div className="stock-research-range-summary-grid">
                  <div className="stock-research-range-summary-card">
                    <div className="stock-research-range-summary-label">Start Price</div>
                    <div className="stock-research-range-summary-value">
                      ${getRangeSummary().startPrice.toFixed(2)}
                    </div>
                  </div>
                  <div className="stock-research-range-summary-card">
                    <div className="stock-research-range-summary-label">End Price</div>
                    <div className="stock-research-range-summary-value">
                      ${getRangeSummary().endPrice.toFixed(2)}
                    </div>
                  </div>
                  <div className="stock-research-range-summary-card">
                    <div className="stock-research-range-summary-label">Total Return</div>
                    <div className={`stock-research-range-summary-value ${getRangeSummary().totalReturn >= 0 ? 'positive' : 'negative'}`}>
                      {getRangeSummary().totalReturn >= 0 ? '+' : ''}{getRangeSummary().totalReturn.toFixed(2)}%
                    </div>
                  </div>
                  <div className="stock-research-range-summary-card">
                    <div className="stock-research-range-summary-label">Max Drawdown</div>
                    <div className={`stock-research-range-summary-value ${
                      getRangeSummary().maxDrawdown > 60 ? 'negative' : 
                      getRangeSummary().maxDrawdown > 35 ? 'warning' : 'positive'
                    }`}>
                      {getRangeSummary().maxDrawdown.toFixed(2)}%
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fundamentals Section */}
          <div className="stock-research-section">
            <h3 className="stock-research-section-title">
              Fundamentals
            </h3>
            <p className="stock-research-section-subtitle">
              Latest available fundamentals data.
            </p>

            {!stockData.fundamentals ? (
              <div className="stock-research-no-data">
                <p>
                  No fundamentals data available for this stock.
                </p>
              </div>
            ) : (
              <>
                {isFreePlan && (
                  <p className="stock-research-fundamentals-note">
                    Free Preview shows core valuation fundamentals only. Upgrade to Pro or Premium for profitability, balance sheet, liquidity, growth, and cash flow metrics.
                  </p>
                )}
                <div className="stock-research-fundamentals-grid">
                {/* Valuation Card */}
                <div className="stock-research-fundamentals-card">
                  <h4 className="stock-research-fundamentals-card-title">
                    Valuation
                  </h4>
                  <table className="stock-research-fundamentals-table">
                    <tbody>
                      <tr>
                        <td className="stock-research-fundamentals-label">Market Cap</td>
                        <td className="stock-research-fundamentals-value">{formatCurrency(stockData.fundamentals.market_cap)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">Enterprise Value</td>
                        <td className="stock-research-fundamentals-value">{formatCurrency(stockData.fundamentals.enterprise_value)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">P/E Ratio</td>
                        <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.trailing_pe)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">P/B Ratio</td>
                        <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.price_to_book)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">P/S Ratio</td>
                        <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.price_to_sales)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">EV/EBITDA</td>
                        <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.enterprise_to_ebitda)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">EV/Revenue</td>
                        <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.enterprise_to_revenue)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">Book Value</td>
                        <td className="stock-research-fundamentals-value">{formatCurrency(stockData.fundamentals.book_value)}</td>
                      </tr>
                      <tr>
                        <td className="stock-research-fundamentals-label">Earnings Per Share</td>
                        <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.earnings_per_share)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && stockData.fundamentals.valuation_interpretation && (
                    <p className="stock-research-fundamentals-interpretation">
                      {stockData.fundamentals.valuation_interpretation}
                    </p>
                  )}
                </div>

                {/* Profitability Card - Pro/Premium only */}
                {!isFreePlan && (
                  <div className="stock-research-fundamentals-card">
                    <h4 className="stock-research-fundamentals-card-title">
                      Profitability
                    </h4>
                    <table className="stock-research-fundamentals-table">
                      <tbody>
                        <tr>
                          <td className="stock-research-fundamentals-label">EBITDA</td>
                          <td className="stock-research-fundamentals-value">{formatCurrency(stockData.fundamentals.ebitda)}</td>
                        </tr>
                        <tr>
                          <td className="stock-research-fundamentals-label">Gross Profit</td>
                          <td className="stock-research-fundamentals-value">{formatCurrency(stockData.fundamentals.gross_profit)}</td>
                        </tr>
                        <tr>
                          <td className="stock-research-fundamentals-label">Return on Equity (ROE)</td>
                          <td className="stock-research-fundamentals-value">{formatPercent(stockData.fundamentals.return_on_equity)}</td>
                        </tr>
                        <tr>
                          <td className="stock-research-fundamentals-label">Operating Margin</td>
                          <td className="stock-research-fundamentals-value">{formatPercent(stockData.fundamentals.operating_margin)}</td>
                        </tr>
                      </tbody>
                    </table>
                    {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && stockData.fundamentals.profitability_interpretation && (
                      <p className="stock-research-fundamentals-interpretation">
                        {stockData.fundamentals.profitability_interpretation}
                      </p>
                    )}
                  </div>
                )}

                {/* Balance Sheet / Liquidity Card - Pro/Premium only */}
                {!isFreePlan && (
                  <div className="stock-research-fundamentals-card">
                    <h4 className="stock-research-fundamentals-card-title">
                      Balance Sheet / Liquidity
                    </h4>
                    <table className="stock-research-fundamentals-table">
                      <tbody>
                        <tr>
                          <td className="stock-research-fundamentals-label">Debt to Equity</td>
                          <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.debt_to_equity)}</td>
                        </tr>
                        <tr>
                          <td className="stock-research-fundamentals-label">Current Ratio</td>
                          <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.current_ratio)}</td>
                        </tr>
                        <tr>
                          <td className="stock-research-fundamentals-label">Quick Ratio</td>
                          <td className="stock-research-fundamentals-value">{formatNumber(stockData.fundamentals.quick_ratio)}</td>
                        </tr>
                      </tbody>
                    </table>
                    {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && stockData.fundamentals.balance_sheet_interpretation && (
                      <p className="stock-research-fundamentals-interpretation">
                        {stockData.fundamentals.balance_sheet_interpretation}
                      </p>
                    )}
                  </div>
                )}

                {/* Growth / Cash Flow Card - Pro/Premium only */}
                {!isFreePlan && (
                  <div className="stock-research-fundamentals-card">
                    <h4 className="stock-research-fundamentals-card-title">
                      Growth / Cash Flow
                    </h4>
                    <table className="stock-research-fundamentals-table">
                      <tbody>
                        <tr>
                          <td className="stock-research-fundamentals-label">Free Cash Flow</td>
                          <td className="stock-research-fundamentals-value">{formatCurrency(stockData.fundamentals.free_cash_flow)}</td>
                        </tr>
                        <tr>
                          <td className="stock-research-fundamentals-label">Revenue Growth</td>
                          <td className="stock-research-fundamentals-value">{formatPercent(stockData.fundamentals.revenue_growth)}</td>
                        </tr>
                      </tbody>
                    </table>
                    {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && stockData.fundamentals.growth_cashflow_interpretation && (
                      <p className="stock-research-fundamentals-interpretation">
                        {stockData.fundamentals.growth_cashflow_interpretation}
                      </p>
                    )}
                  </div>
                )}
                </div>
              </>
            )}
          </div>

          {/* Fundamentals Interpretation Teaser - Pro users only */}
          {!loadingUserInfo && userInfo && userInfo.plan_type === 'pro' && stockData.fundamentals && (
            <div className="stock-research-premium-teaser">
              <h3 className="stock-research-premium-teaser-title">
                Fundamentals Interpretation
              </h3>
              <p className="stock-research-premium-teaser-text">
                Premium adds written interpretation of the valuation, profitability, balance-sheet, and growth/cash-flow figures shown above, explaining what the numbers suggest together rather than leaving them as raw metrics only.
              </p>
              <p className="stock-research-premium-teaser-note">
                Available on Premium.
              </p>
            </div>
          )}
        </div>
      )}
      </div>
  );
}

export default StockResearch;
